import { ApolloProvider } from "@apollo/client";
import {
  AuthModeProvider,
  CustomIntlProvider,
  ImportExportAuthGuard,
  ImportModal as SharedImportModal,
  ImportSpecType,
} from "@flaminem/import-export";
import "@flaminem/import-export/dist/style.css";
import { FC } from "react";
import { useNavigate } from "react-router-dom";

import { AppUser } from "src/authentication/appUser";
import { useToast } from "src/components/Toasts/ToastContainer.tsx";
import { createApolloClient } from "src/graphql/import-export/apolloClient.ts";
import { emitError } from "src/hooks/useEvents.ts";
import { useT } from "src/internationalization";
import { CaseType } from "src/types/case";
import { ImportSpecificationType } from "src/types/importSpecification";

export type ImportProps = {
  onClose: () => void;
  importSpecType: ImportSpecificationType;
  caseType?: CaseType;
};

const apolloClient = createApolloClient(emitError);

// Helper function to map local ImportSpecificationType to shared ImportSpecType
const mapToSharedImportSpecType = (type: ImportSpecificationType): ImportSpecType => {
  return type === "case" ? ImportSpecType.Case : ImportSpecType.Blacklist;
};

const ImportModal: FC<ImportProps> = ({ onClose, importSpecType, caseType }) => {
  const { showSuccessToast } = useToast();
  const t = useT();
  const navigate = useNavigate();

  const handleSuccess = (jobId: string) => {
    showSuccessToast(t("import.modal.toast.success.message"));
    navigate(`/jobs/${jobId}`);
  };

  const titleType =
    importSpecType === "case"
      ? t("import.modal.title.type.cases")
      : t("import.modal.title.type.blacklist");

  const modalHeading = t("import.modal.title", { type: titleType });
  const subtitle = importSpecType === "case" && caseType ? caseType.name : undefined;
  const confirmButtonText = t("import.button.import");
  const languageTexts = {
    cancel: t("general.actions.cancel"),
    close: t("general.actions.close"),
  };

  return (
    <ApolloProvider client={apolloClient}>
      <AuthModeProvider authMode="bearer">
        <ImportExportAuthGuard getToken={() => AppUser.getAccessToken() ?? null}>
          <CustomIntlProvider>
            <SharedImportModal
              onClose={onClose}
              importSpecType={mapToSharedImportSpecType(importSpecType)}
              caseType={caseType}
              onSuccess={handleSuccess}
              modalHeading={modalHeading}
              subtitle={subtitle}
              confirmButtonText={confirmButtonText}
              closeOnOutsideClick={false}
              closeOnEscape={false}
              initialFocus="#importSpecificationPicker"
              languageTexts={languageTexts}
            />
          </CustomIntlProvider>
        </ImportExportAuthGuard>
      </AuthModeProvider>
    </ApolloProvider>
  );
};

export default ImportModal;
